import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "../components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table";
import { toast } from "sonner";
import {
  Loader2,
  Calendar,
  Users,
  ChevronLeft,
  ChevronRight,
  Plus,
  Sun,
  Moon,
  Sunset,
  Coffee,
  Palmtree,
  HeartPulse,
  CheckCircle2,
  XCircle,
  Clock,
  Download,
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SHIFT_TYPES = {
  morning: { label: "Manhã", icon: Sun, color: "bg-amber-100 text-amber-800 border-amber-300" },
  afternoon: { label: "Tarde", icon: Sunset, color: "bg-orange-100 text-orange-800 border-orange-300" },
  night: { label: "Noite", icon: Moon, color: "bg-indigo-100 text-indigo-800 border-indigo-300" },
  off: { label: "Folga", icon: Coffee, color: "bg-gray-100 text-gray-800 border-gray-300" },
  vacation: { label: "Férias", icon: Palmtree, color: "bg-green-100 text-green-800 border-green-300" },
  sick: { label: "Baixa", icon: HeartPulse, color: "bg-red-100 text-red-800 border-red-300" },
};

const WEEKDAYS = ['Dom', 'Seg', 'Ter', 'Qua', 'Qui', 'Sex', 'Sáb'];

export default function ShiftManagement() {
  const [loading, setLoading] = useState(true);
  const [users, setUsers] = useState([]);
  const [schedules, setSchedules] = useState([]);
  const [timeOffRequests, setTimeOffRequests] = useState([]);
  const [selectedWeek, setSelectedWeek] = useState(getWeekStart(new Date()));
  
  // Dialogs
  const [requestDialogOpen, setRequestDialogOpen] = useState(false);
  const [scheduleDialogOpen, setScheduleDialogOpen] = useState(false);
  
  const [selectedUser, setSelectedUser] = useState(null);
  const [selectedDate, setSelectedDate] = useState(null);
  
  const [requestForm, setRequestForm] = useState({
    start_date: "",
    end_date: "",
    request_type: "vacation",
    reason: ""
  });

  function getWeekStart(date) {
    const d = new Date(date);
    const day = d.getDay();
    const diff = d.getDate() - day;
    return new Date(d.setDate(diff));
  }

  function getWeekDates(startDate) {
    const dates = [];
    for (let i = 0; i < 7; i++) {
      const date = new Date(startDate);
      date.setDate(date.getDate() + i);
      dates.push(date);
    }
    return dates;
  }

  function formatDate(date) {
    return date.toISOString().split('T')[0];
  }

  function formatDateDisplay(date) {
    return date.toLocaleDateString('pt-PT', { day: '2-digit', month: '2-digit' });
  }

  const fetchData = useCallback(async () => {
    try {
      const weekDates = getWeekDates(selectedWeek);
      const startDate = formatDate(weekDates[0]);
      const endDate = formatDate(weekDates[6]);
      
      const [usersRes, schedulesRes, requestsRes] = await Promise.all([
        axios.get(`${API}/users`),
        axios.get(`${API}/shift-schedules?start_date=${startDate}&end_date=${endDate}`),
        axios.get(`${API}/time-off-requests`),
      ]);
      
      // Filter only operators and shift leaders
      setUsers(usersRes.data.filter(u => u.role === 'operator' || u.role === 'shift_leader'));
      setSchedules(schedulesRes.data);
      setTimeOffRequests(requestsRes.data);
    } catch (error) {
      console.error("Error fetching data:", error);
      toast.error("Erro ao carregar dados");
    } finally {
      setLoading(false);
    }
  }, [selectedWeek]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const getScheduleForUserDate = (userId, date) => {
    const dateStr = formatDate(date);
    return schedules.find(s => s.user_id === userId && s.date === dateStr);
  };

  const handleCellClick = (user, date) => {
    setSelectedUser(user);
    setSelectedDate(date);
    setScheduleDialogOpen(true);
  };

  const handleSetShift = async (shiftType) => {
    try {
      await axios.post(`${API}/shift-schedules`, {
        user_id: selectedUser.id,
        date: formatDate(selectedDate),
        shift_type: shiftType
      });
      toast.success("Escala atualizada!");
      setScheduleDialogOpen(false);
      fetchData();
    } catch (error) {
      toast.error("Erro ao atualizar escala");
    }
  };

  const handleCreateRequest = async () => {
    try {
      await axios.post(`${API}/time-off-requests`, requestForm);
      toast.success("Pedido criado com sucesso!");
      setRequestDialogOpen(false);
      setRequestForm({ start_date: "", end_date: "", request_type: "vacation", reason: "" });
      fetchData();
    } catch (error) {
      toast.error("Erro ao criar pedido");
    }
  };

  const handleApproveRequest = async (requestId) => {
    try {
      await axios.put(`${API}/time-off-requests/${requestId}/approve`);
      toast.success("Pedido aprovado!");
      fetchData();
    } catch (error) {
      toast.error("Erro ao aprovar pedido");
    }
  };

  const handleRejectRequest = async (requestId) => {
    const reason = prompt("Motivo da rejeição:");
    if (!reason) return;
    
    try {
      await axios.put(`${API}/time-off-requests/${requestId}/reject?reason=${encodeURIComponent(reason)}`);
      toast.success("Pedido rejeitado");
      fetchData();
    } catch (error) {
      toast.error("Erro ao rejeitar pedido");
    }
  };

  const weekDates = getWeekDates(selectedWeek);

  const exportToPDF = () => {
    // Build schedule lookup from schedules array
    const scheduleMap = {};
    schedules.forEach(s => {
      if (!scheduleMap[s.user_id]) scheduleMap[s.user_id] = {};
      scheduleMap[s.user_id][s.date] = s.shift_type;
    });

    // Filter only users that have schedules in this week
    const weekDateStrings = weekDates.map(d => d.toISOString().split('T')[0]);
    const usersWithSchedules = users.filter(user => {
      // Check if user has any schedule in this week
      return weekDateStrings.some(dateStr => scheduleMap[user.id]?.[dateStr]);
    });

    if (usersWithSchedules.length === 0) {
      alert("Não há operadores com turnos marcados nesta semana para exportar.");
      return;
    }

    const printContent = `
      <!DOCTYPE html>
      <html>
      <head>
        <title>Escala de Turnos - ${formatDateDisplay(weekDates[0])} a ${formatDateDisplay(weekDates[6])}</title>
        <style>
          * { margin: 0; padding: 0; box-sizing: border-box; }
          body { font-family: 'Segoe UI', Arial, sans-serif; padding: 30px; color: #1a1a1a; }
          .header { display: flex; justify-content: space-between; align-items: center; border-bottom: 3px solid #2563eb; padding-bottom: 15px; margin-bottom: 20px; }
          .header h1 { font-size: 24px; color: #1e293b; }
          .header .date { font-size: 14px; color: #64748b; }
          .logo { height: 50px; }
          table { width: 100%; border-collapse: collapse; margin-top: 15px; }
          th { background: #2563eb; color: white; padding: 12px 8px; text-align: center; font-size: 12px; }
          td { border: 1px solid #e2e8f0; padding: 10px 8px; text-align: center; font-size: 11px; }
          .operator-name { text-align: left; font-weight: 600; background: #f8fafc; }
          .morning { background: #fef3c7; color: #92400e; }
          .afternoon { background: #fed7aa; color: #9a3412; }
          .night { background: #e0e7ff; color: #3730a3; }
          .off { background: #f3f4f6; color: #6b7280; }
          .vacation { background: #d1fae5; color: #065f46; }
          .sick { background: #fee2e2; color: #991b1b; }
          .footer { margin-top: 30px; padding-top: 15px; border-top: 1px solid #e2e8f0; font-size: 10px; color: #94a3b8; display: flex; justify-content: space-between; }
          .legend { margin-top: 20px; display: flex; flex-wrap: wrap; gap: 15px; font-size: 11px; }
          .legend-item { display: flex; align-items: center; gap: 5px; }
          .legend-box { width: 20px; height: 20px; border-radius: 4px; }
        </style>
      </head>
      <body>
        <div class="header">
          <div>
            <h1>Escala de Turnos</h1>
            <div class="date">Semana: ${formatDateDisplay(weekDates[0])} a ${formatDateDisplay(weekDates[6])}</div>
          </div>
          <img src="https://i.imgur.com/8GsQHbN.png" alt="Logo" class="logo" />
        </div>
        
        <table>
          <thead>
            <tr>
              <th style="width: 150px;">Operador</th>
              ${weekDates.map((date, i) => `
                <th>${WEEKDAYS[date.getDay()]}<br/>${date.getDate()}/${date.getMonth() + 1}</th>
              `).join('')}
            </tr>
          </thead>
          <tbody>
            ${usersWithSchedules.map(user => `
              <tr>
                <td class="operator-name">${user.name}<br/><small style="color:#64748b">${user.user_code}</small></td>
                ${weekDates.map(date => {
                  const dateStr = date.toISOString().split('T')[0];
                  const shift = scheduleMap[user.id]?.[dateStr] || '-';
                  const shiftInfo = SHIFT_TYPES[shift] || { label: '-' };
                  return `<td class="${shift}">${shiftInfo.label}</td>`;
                }).join('')}
              </tr>
            `).join('')}
          </tbody>
        </table>
        
        <div class="legend">
          <div class="legend-item"><div class="legend-box morning"></div> Manhã</div>
          <div class="legend-item"><div class="legend-box afternoon"></div> Tarde</div>
          <div class="legend-item"><div class="legend-box night"></div> Noite</div>
          <div class="legend-item"><div class="legend-box off"></div> Folga</div>
          <div class="legend-item"><div class="legend-box vacation"></div> Férias</div>
          <div class="legend-item"><div class="legend-box sick"></div> Baixa</div>
        </div>
        
        <div class="footer">
          <span>EP3D ENGINEERING PROJECT - Sistema MES</span>
          <span>Gerado em: ${new Date().toLocaleString('pt-PT')}</span>
        </div>
      </body>
      </html>
    `;

    const printWindow = window.open('', '_blank');
    printWindow.document.write(printContent);
    printWindow.document.close();
    printWindow.onload = () => {
      printWindow.print();
    };
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="shift-management-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="font-chivo text-3xl md:text-4xl font-bold tracking-tight text-foreground">
            Gestão de Turnos
          </h1>
          <p className="text-muted-foreground mt-1">
            Escalas, folgas e férias dos operadores
          </p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={exportToPDF}>
            <Download className="h-4 w-4 mr-2" />
            Exportar PDF
          </Button>
          <Button onClick={() => setRequestDialogOpen(true)}>
            <Plus className="h-4 w-4 mr-2" />
            Pedir Ausência
          </Button>
        </div>
      </div>

      {/* Week Navigation */}
      <Card>
        <CardContent className="py-4">
          <div className="flex items-center justify-between">
            <Button
              variant="outline"
              onClick={() => {
                const newDate = new Date(selectedWeek);
                newDate.setDate(newDate.getDate() - 7);
                setSelectedWeek(newDate);
              }}
            >
              <ChevronLeft className="h-4 w-4 mr-1" />
              Semana Anterior
            </Button>
            <div className="text-center">
              <h2 className="text-xl font-bold">
                Semana {formatDateDisplay(weekDates[0])} - {formatDateDisplay(weekDates[6])}
              </h2>
              <p className="text-sm text-muted-foreground">
                Clique numa célula para definir o turno
              </p>
            </div>
            <Button
              variant="outline"
              onClick={() => {
                const newDate = new Date(selectedWeek);
                newDate.setDate(newDate.getDate() + 7);
                setSelectedWeek(newDate);
              }}
            >
              Semana Seguinte
              <ChevronRight className="h-4 w-4 ml-1" />
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Schedule Grid */}
      <Card>
        <CardHeader className="bg-gradient-to-r from-blue-600 to-blue-700 text-white rounded-t-lg">
          <CardTitle className="font-chivo flex items-center gap-2">
            <Calendar className="h-5 w-5" />
            Escala Semanal
          </CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="bg-gray-50 border-b">
                  <th className="p-3 text-left font-semibold text-gray-700 w-48 border-r">
                    Operador
                  </th>
                  {weekDates.map((date, idx) => (
                    <th key={idx} className="p-3 text-center font-semibold text-gray-700 border-r min-w-[100px]">
                      <div className="text-sm">{WEEKDAYS[idx]}</div>
                      <div className="text-xs text-gray-500">{formatDateDisplay(date)}</div>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {users.map((user) => (
                  <tr key={user.id} className="border-b hover:bg-gray-50/50">
                    <td className="p-3 border-r bg-gray-50">
                      <div className="font-bold text-gray-800">{user.name}</div>
                      <div className="text-xs text-gray-500">{user.user_code}</div>
                      <Badge variant="outline" className="mt-1 text-xs">
                        {user.role === 'shift_leader' ? 'Chefe Turno' : 'Operador'}
                      </Badge>
                    </td>
                    {weekDates.map((date, dayIndex) => {
                      const schedule = getScheduleForUserDate(user.id, date);
                      const shiftType = schedule?.shift_type || 'off';
                      const ShiftIcon = SHIFT_TYPES[shiftType]?.icon || Coffee;
                      const shiftConfig = SHIFT_TYPES[shiftType] || SHIFT_TYPES.off;
                      
                      return (
                        <td 
                          key={dayIndex}
                          className={`p-2 border-r text-center cursor-pointer hover:bg-blue-50 transition-colors ${
                            dayIndex === 0 || dayIndex === 6 ? 'bg-gray-100' : ''
                          }`}
                          onClick={() => handleCellClick(user, date)}
                        >
                          {schedule ? (
                            <div className={`inline-flex items-center gap-1 px-2 py-1 rounded-full border ${shiftConfig.color}`}>
                              <ShiftIcon className="h-3 w-3" />
                              <span className="text-xs font-medium">{shiftConfig.label}</span>
                            </div>
                          ) : (
                            <span className="text-gray-300 text-xs">-</span>
                          )}
                        </td>
                      );
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>

      {/* Legend */}
      <div className="flex flex-wrap gap-3 justify-center">
        {Object.entries(SHIFT_TYPES).map(([key, config]) => {
          const Icon = config.icon;
          return (
            <div key={key} className={`inline-flex items-center gap-1 px-3 py-1 rounded-full border ${config.color}`}>
              <Icon className="h-4 w-4" />
              <span className="text-sm">{config.label}</span>
            </div>
          );
        })}
      </div>

      {/* Time Off Requests */}
      <Card>
        <CardHeader>
          <CardTitle className="font-chivo flex items-center gap-2">
            <Clock className="h-5 w-5" />
            Pedidos de Ausência
          </CardTitle>
        </CardHeader>
        <CardContent>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Data Pedido</TableHead>
                <TableHead>Colaborador</TableHead>
                <TableHead>Tipo</TableHead>
                <TableHead>Período</TableHead>
                <TableHead>Motivo</TableHead>
                <TableHead>Estado</TableHead>
                <TableHead>Ações</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {timeOffRequests.map((request) => (
                <TableRow key={request.id}>
                  <TableCell>
                    {new Date(request.created_at).toLocaleDateString('pt-PT')}
                  </TableCell>
                  <TableCell>
                    <div className="font-medium">{request.user_name}</div>
                    <div className="text-xs text-muted-foreground">{request.user_code}</div>
                  </TableCell>
                  <TableCell>
                    <Badge variant="outline">
                      {request.request_type === 'vacation' ? 'Férias' :
                       request.request_type === 'sick' ? 'Baixa' :
                       request.request_type === 'personal' ? 'Pessoal' : 'Outro'}
                    </Badge>
                  </TableCell>
                  <TableCell>
                    {new Date(request.start_date).toLocaleDateString('pt-PT')} - {new Date(request.end_date).toLocaleDateString('pt-PT')}
                  </TableCell>
                  <TableCell className="max-w-xs truncate">{request.reason || '-'}</TableCell>
                  <TableCell>
                    <Badge className={
                      request.status === 'approved' ? 'bg-green-500' :
                      request.status === 'rejected' ? 'bg-red-500' :
                      'bg-amber-500'
                    }>
                      {request.status === 'approved' ? 'Aprovado' :
                       request.status === 'rejected' ? 'Rejeitado' : 'Pendente'}
                    </Badge>
                  </TableCell>
                  <TableCell>
                    {request.status === 'pending' && (
                      <div className="flex gap-2">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleApproveRequest(request.id)}
                        >
                          <CheckCircle2 className="h-4 w-4 text-green-500" />
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleRejectRequest(request.id)}
                        >
                          <XCircle className="h-4 w-4 text-red-500" />
                        </Button>
                      </div>
                    )}
                  </TableCell>
                </TableRow>
              ))}
              {timeOffRequests.length === 0 && (
                <TableRow>
                  <TableCell colSpan={7} className="text-center text-muted-foreground py-8">
                    Sem pedidos de ausência
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Set Shift Dialog */}
      <Dialog open={scheduleDialogOpen} onOpenChange={setScheduleDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              Definir Turno - {selectedUser?.name}
            </DialogTitle>
          </DialogHeader>
          <div className="text-center mb-4">
            <span className="text-lg font-medium">
              {selectedDate && formatDateDisplay(selectedDate)}
            </span>
          </div>
          <div className="grid grid-cols-2 gap-3">
            {Object.entries(SHIFT_TYPES).map(([key, config]) => {
              const Icon = config.icon;
              return (
                <Button
                  key={key}
                  variant="outline"
                  className={`h-16 flex flex-col items-center gap-1 ${config.color} hover:opacity-80`}
                  onClick={() => handleSetShift(key)}
                >
                  <Icon className="h-5 w-5" />
                  <span>{config.label}</span>
                </Button>
              );
            })}
          </div>
        </DialogContent>
      </Dialog>

      {/* Request Dialog */}
      <Dialog open={requestDialogOpen} onOpenChange={setRequestDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Pedir Ausência</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Tipo de Ausência</Label>
              <Select
                value={requestForm.request_type}
                onValueChange={(v) => setRequestForm({ ...requestForm, request_type: v })}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="vacation">Férias</SelectItem>
                  <SelectItem value="sick">Baixa Médica</SelectItem>
                  <SelectItem value="personal">Pessoal</SelectItem>
                  <SelectItem value="other">Outro</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Data Início</Label>
                <Input
                  type="date"
                  value={requestForm.start_date}
                  onChange={(e) => setRequestForm({ ...requestForm, start_date: e.target.value })}
                />
              </div>
              <div>
                <Label>Data Fim</Label>
                <Input
                  type="date"
                  value={requestForm.end_date}
                  onChange={(e) => setRequestForm({ ...requestForm, end_date: e.target.value })}
                />
              </div>
            </div>
            <div>
              <Label>Motivo (opcional)</Label>
              <Textarea
                value={requestForm.reason}
                onChange={(e) => setRequestForm({ ...requestForm, reason: e.target.value })}
                placeholder="Descreva o motivo..."
              />
            </div>
            <Button 
              onClick={handleCreateRequest} 
              className="w-full"
              disabled={!requestForm.start_date || !requestForm.end_date}
            >
              Submeter Pedido
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
